import React, { useCallback, useEffect, useState } from 'react';
import { useMicrophone } from '../hooks/useMicrophone';
import { useVAD } from '../hooks/useVAD';
import { useTurnTakingMachine } from '../hooks/useTurnTakingMachine';
import { useStreamingSTT } from '../hooks/useStreamingSTT';
import { useStreamingTTS } from '../hooks/useStreamingTTS';
import { API_ENDPOINTS } from '../config/api';
import './VoiceController.css';

/**
 * VoiceController Component
 *
 * Main orchestrator for hands-free AI voice interaction
 * Coordinates microphone, VAD, state machine, STT, and TTS
 *
 * Flow:
 * 1. Mic is always on
 * 2. VAD detects speech start → USER_SPEAKING state
 * 3. VAD detects speech end → PROCESSING state → send to backend
 * 4. Backend responds → AI_SPEAKING state → play TTS
 * 5. TTS ends → IDLE state → ready for next interaction
 * 6. User can interrupt AI at any time
 */
const VoiceController = ({ sessionId, orderState, setOrderState, onEndCall }) => {
  const [conversationHistory, setConversationHistory] = useState([]);
  const [partialTranscript, setPartialTranscript] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const callStartTime = React.useRef(Date.now());
  const hasInitialized = React.useRef(false);
  const shouldEndAfterSpeaking = React.useRef(false);

  // Initialize microphone
  const { stream, analyser, isActive: micActive, error: micError, stopMicrophone } = useMicrophone();

  // Initialize state machine
  const {
    state,
    vadEnabled,
    send,
    isUserSpeaking,
    isProcessing,
    isAISpeaking,
    isIdle,
    EVENTS
  } = useTurnTakingMachine();

  // Handle speech start (from VAD)
  const handleSpeechStart = useCallback(() => {
    console.log('🎙️ User started speaking');

    // If AI is speaking, this will trigger interruption (state machine handles it)
    // Otherwise, transition from IDLE to USER_SPEAKING
    send(EVENTS.SPEECH_START);
  }, [send, EVENTS]);

  // Handle speech end (from VAD)
  const handleSpeechEnd = useCallback(() => {
    console.log('🔇 User stopped speaking');
    send(EVENTS.SPEECH_END);
  }, [send, EVENTS]);

  // Initialize VAD with intelligent dual-mode thresholds
  // CRITICAL: High threshold during AI speech (ignore background noise)
  // Low threshold when listening (sensitive to user)
  useVAD(analyser, handleSpeechStart, handleSpeechEnd, {
    enabled: micActive,  // Always enabled
    silenceDuration: 400,  // Faster cutoff (400ms)
    energyThreshold: isAISpeaking ? 0.12 : 0.025,  // 0.12 during AI (high), 0.025 when listening (low)
    minSpeechDuration: isAISpeaking ? 400 : 150  // Require 400ms clear speech to interrupt, 150ms normally
  });

  // TTS callbacks (defined early to be used in processUserInput)
  const handleTTSStart = useCallback(() => {
    console.log('🔊 AI started speaking');
  }, []);

  const handleTTSComplete = useCallback(() => {
    console.log('✅ AI finished speaking');
    send(EVENTS.AI_SPEECH_COMPLETE);

    // Check if call should end after speaking
    if (shouldEndAfterSpeaking.current) {
      console.log('🔚 Ending call now (as requested by end_call action)');
      shouldEndAfterSpeaking.current = false;

      // Small delay to ensure TTS completes and state updates
      setTimeout(() => {
        console.log('📞 Auto-ending call...');
        onEndCall();
      }, 1000);
    }
  }, [send, EVENTS, onEndCall]);

  // Initialize TTS (before processUserInput so speakText is available)
  const {
    speakText,
    stopTTS,
    isSpeaking
  } = useStreamingTTS({
    onSpeechStart: handleTTSStart,
    onSpeechEnd: handleTTSComplete,
    rate: 1.1
  });

  // Process user input (send to backend)
  const processUserInput = useCallback(async (transcript) => {
    if (!transcript || !transcript.trim()) return;

    const startTime = Date.now();
    console.log('📤 Processing user input:', transcript);

    // Add user message to history
    setConversationHistory(prev => [...prev, {
      type: 'user',
      text: transcript,
      timestamp: new Date().toISOString()
    }]);

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      console.log('⏱️  Request timeout after 8 seconds');
    }, 8000);

    try {
      const fetchStart = Date.now();
      const response = await fetch(API_ENDPOINTS.voiceOrder, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          input: transcript,
          orderState
        }),
        signal: controller.signal  // Add abort signal for timeout
      });

      // Clear timeout on successful response
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const fetchDuration = Date.now() - fetchStart;
      const totalDuration = Date.now() - startTime;
      console.log(`\n━━━━━━ BACKEND RESPONSE (${fetchDuration}ms) ━━━━━━`);
      console.log('💬 AI message:', data.response);
      console.log('🛒 Cart items:', data.orderState?.items?.length
        ? data.orderState.items.map(i => `${i.quantity}x ${i.name}`).join(', ')
        : 'EMPTY');
      console.log('💰 Total:', data.orderState?.total ?? 'N/A', 'AED');
      console.log('🎬 Actions:', JSON.stringify(data.actions));
      console.log('📦 Full orderState:', JSON.stringify(data.orderState));
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

      // Add AI response to history
      setConversationHistory(prev => [...prev, {
        type: 'assistant',
        text: data.response,
        intent: data.intent,
        confidence: data.confidence,
        timestamp: new Date().toISOString()
      }]);

      // Update order state
      setOrderState(data.orderState);

      // Check if call should end after this response
      const shouldEndCall = data.actions && data.actions.includes('end_call');
      if (shouldEndCall) {
        console.log('🔚 Call will end after AI finishes speaking');
        shouldEndAfterSpeaking.current = true;
      }

      // Transition to AI_SPEAKING state
      send(EVENTS.AI_RESPONSE_READY);

      // Speak AI response
      speakText(data.response);

    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);

      console.error('❌ Error processing input:', error);

      // Categorize error type for user-friendly messaging
      let errorMessage;
      if (error.name === 'AbortError') {
        errorMessage = "I'm sorry, the connection timed out. Please try again.";
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = "I'm having trouble connecting to our system. Please check your connection and try again.";
      } else if (error.message.includes('500')) {
        errorMessage = "I encountered an error processing your request. Could you please repeat that?";
      } else if (error.message.includes('404')) {
        errorMessage = "I'm sorry, your session has expired. Please refresh the page to start a new call.";
      } else {
        errorMessage = "Sorry, something went wrong. Please try again.";
      }

      // Add error to conversation history
      setConversationHistory(prev => [...prev, {
        type: 'error',
        text: errorMessage,
        timestamp: new Date().toISOString()
      }]);

      // Transition to AI_SPEAKING to deliver error message via TTS
      send(EVENTS.ERROR_OCCURRED);

      // Speak error message (don't leave user in silence!)
      speakText(errorMessage);
    }
  }, [sessionId, setOrderState, send, EVENTS, speakText]);

  // STT callbacks
  const handlePartialTranscript = useCallback((transcript) => {
    setPartialTranscript(transcript);

    // CRITICAL: If we get coherent words while AI is speaking, confirm it's real interruption
    if (isAISpeaking && transcript && transcript.trim().length > 3) {
      console.log('✅ Confirmed user interruption (got words:', transcript.substring(0, 20) + '...)');
      // Partial transcript confirms user is speaking - good interruption
    }
  }, [isAISpeaking]);

  const handleFinalTranscript = useCallback((transcript) => {
    console.log('✅ Final transcript received:', transcript);
    setPartialTranscript('');

    // CRITICAL: Ensure we transition to PROCESSING state before processing input
    // STT can finish before VAD detects silence, so we need to manually trigger SPEECH_END
    if (isUserSpeaking) {
      console.log('🔄 STT finished, transitioning to PROCESSING state');
      send(EVENTS.SPEECH_END);
    }

    processUserInput(transcript);
  }, [processUserInput, isUserSpeaking, send, EVENTS]);

  // Initialize STT
  const {
    startSTT,
    stopSTT,
    abortSTT
  } = useStreamingSTT({
    onPartialTranscript: handlePartialTranscript,
    onFinalTranscript: handleFinalTranscript
  });

  // Manage STT lifecycle based on state changes
  useEffect(() => {
    if (isUserSpeaking) {
      console.log('🎙️ Starting STT (user is speaking)');
      startSTT();
    } else if (isProcessing) {
      console.log('🛑 Stopping STT (processing)');
      stopSTT();
    }
  }, [isUserSpeaking, isProcessing, startSTT, stopSTT]);

  // Handle user interruption - CRITICAL for natural conversation
  useEffect(() => {
    if (isUserSpeaking && isSpeaking) {
      console.log('⚠️ User interrupted AI - stopping TTS immediately');
      stopTTS();
    }
  }, [isUserSpeaking, isSpeaking, stopTTS]);

  // Initial greeting
  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (hasInitialized.current) {
      console.log('⚠️ VoiceController already initialized, skipping');
      return;
    }
    hasInitialized.current = true;

    const greeting = "Hello! Thank you for calling AI Order Call. My name is Leila, and I'll be happy to help you with your order today. What can I get started for you?";

    setConversationHistory([{
      type: 'assistant',
      text: greeting,
      timestamp: new Date().toISOString()
    }]);

    // Small delay to ensure audio context is ready
    setTimeout(() => {
      send(EVENTS.AI_RESPONSE_READY);
      speakText(greeting);
    }, 500);

    // Cleanup
    return () => {
      console.log('🧹 VoiceController cleanup');
      abortSTT();
      stopTTS();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Call duration timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartTime.current) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle end call - cleanup everything
  const handleEndCall = useCallback(() => {
    console.log('📞 Ending call - cleaning up resources');

    // Stop all audio processing
    abortSTT();
    stopTTS();

    // Release microphone
    stopMicrophone();

    // Notify parent
    onEndCall();
  }, [abortSTT, stopTTS, stopMicrophone, onEndCall]);

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status text
  const getStatusText = () => {
    switch (state) {
      case 'IDLE': return '📞 Listening...';
      case 'USER_SPEAKING': return '🎤 You are speaking...';
      case 'PROCESSING': return '⏳ Processing...';
      case 'AI_SPEAKING': return '🔊 Leila is speaking...';
      default: return '📞 Connected';
    }
  };

  return (
    <div className="voice-controller">
      {/* Header */}
      <div className="voice-header">
        <div className="voice-info">
          <div className="restaurant-icon">🌯</div>
          <div>
            <h2>AI Order Call</h2>
            <p className="voice-status">{getStatusText()}</p>
            {micError && <p className="error">⚠️ {micError}</p>}
          </div>
        </div>
        <div className="call-duration">{formatDuration(callDuration)}</div>
      </div>

      {/* Conversation */}
      <div className="voice-conversation">
        {conversationHistory.map((message, index) => (
          <div key={index} className={`voice-message ${message.type}`}>
            <div className="message-bubble">
              <p>{message.text}</p>
            </div>
          </div>
        ))}

        {/* Show partial transcript while user is speaking */}
        {isUserSpeaking && partialTranscript && (
          <div className="voice-message user partial">
            <div className="message-bubble">
              <p className="partial-text">{partialTranscript}</p>
            </div>
          </div>
        )}
      </div>

      {/* Order Summary */}
      {orderState.items.length > 0 && (
        <div className="voice-order-summary">
          <h3>Current Order</h3>
          <div className="order-items">
            {orderState.items.map((item, index) => (
              <div key={index} className="order-item">
                <span>{item.quantity}x {item.name}</span>
                <span>AED {(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="order-total">
              <strong>Total: AED {orderState.total.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="voice-controls">
        <button className="end-call-btn" onClick={handleEndCall}>
          <span className="phone-icon">📞</span>
          End Call
        </button>
      </div>

      {/* Visual Indicator */}
      {isUserSpeaking && (
        <div className="voice-indicator">
          <div className="pulse-ring"></div>
          <div className="pulse-ring delay-1"></div>
          <div className="pulse-ring delay-2"></div>
          <span>Listening...</span>
        </div>
      )}
    </div>
  );
};

export default VoiceController;
